import store from '@/core/store'
import { useEffect, useState } from 'react'
import authService from '@/app-domains/user/auth/Auth.service'
import commonsService from '@/services/commons.service'

import { ReactComponent as HomeIcon } from '@/assets/icons/home-icon.svg'
import { ReactComponent as LogoutIcon } from '@/assets/icons/logout-solid.svg'
import { ReactComponent as CogIcon } from '@/assets/icons/cog-solid.svg'
import { ReactComponent as QuestionMarkIcon } from '@/assets/icons/question-mark-icon-colorless.svg'
import { ReactComponent as ContactAltIcon } from '@/assets/icons/chat-alt-2-solid.svg'
import { ReactComponent as WhatsNewIcon } from '@/assets/icons/bell_black-singing.svg'

import dropdownService from '@/components/DropdownMenu/dropdown.service'
import modalService from '@/services/modal.service'
import ChangelogModal, { markChangelogViewed, hasUnreadChangelog } from '@/components/ChangelogModal/ChangelogModal'
import FaqModal from '@/components/FaqModal/FaqModal'
import writeMessageModal from '@/app-domains/messages/writing/WritingModal.service'
import writingService from '@/app-domains/messages/draft/writing.service'

import './UserCard.sass'
import sessionService from '@/app-domains/user/session/session.service'
import useRenderEvent from '@/core/hooks/useRenderEvent'

const default_picture_img = 'https://via.placeholder.com/150'

export function UserCard () {
  const [meInfo, setMeInfo] = useState({})
  const [consumedStorage, setConsumedStorage] = useState(0)
  const [fullStorage, setFullStorage] = useState(0)
  const [isMenuShown, setIsMenuShown] = useState(false)
  const [isMenuShownEvent, setIsMenuShownEvent] = useState(undefined)
  const setInfo = () => {
    const me = store.getState().me
    setMeInfo(me)
    setConsumedStorage(me.me?.consumedStorage)
    setFullStorage(me.me?.fullStorage)
  }

  const toggleMenuOpen = (e) => {
    e.stopPropagation()
    if (dropdownService.isOpenedForThisTarget(e)) {
      setIsMenuShown(false)
      setIsMenuShownEvent(false)
      return dropdownService.close()
    }
    setIsMenuShownEvent(e)
    setIsMenuShown(Date.now())
  }

  useEffect(() => {
    setInfo()
    store.on('ME:info_updated', () => {
      setInfo()
    })

    store.on('DROPDOWN:CLOSE', () => {
      setIsMenuShown(false)
      setIsMenuShownEvent(false)
    })
  }, [])

  const consumed = consumedStorage / fullStorage
  // eslint-disable-next-line
  const consumed_level = consumed < 0.35
    ? 'low'
    : (
        consumed < 0.6
          ? 'mid'
          : (
              consumed < 0.85
                ? 'high'
                : 'critical'
            )
      )

  // eslint-disable-next-line
  const width = 100 * consumed + '%'

  return (
    <section className="user-card">
      <div className="me-img" onClick={toggleMenuOpen}>
        <img src={meInfo.me?.profilePictureUrl || default_picture_img} className="profileImageStyle"></img>
      </div>
      { isMenuShown && <UserDropdownMenu event={isMenuShownEvent}/>}
    </section>
  )
}

export function UserDropdownMenu ({ event }) {
  const iconSize = { width: '18px', height: '18px' }
  const [userStatus, setUserStatus] = useState(sessionService.getMe().me.status)
  
  useRenderEvent(['SUBSCRIPTION:CHANGED'], (status) => setUserStatus(status))
  const boxCount = Object.keys(store.getState().boxes?.boxes || {}).length
  const labelCount = Object.keys(store.getState().boxlabels?.labels || {}).length
  const showUnreadDot = hasUnreadChangelog()

  const whatsNewItem = {
    icon: <WhatsNewIcon style={iconSize} />,
    title: showUnreadDot ? "What's new •" : "What's new",
    action: () => {
      markChangelogViewed()
      dropdownService.close()
      modalService.open(<ChangelogModal/>)
    },
    color: 'normal'
  }

  // Support → open the compose modal (same as the compose button) prefilled with
  // From=boxes (→ boxes@<their-domain>; that box is created on send if it doesn't
  // exist yet), To=support@boxes.email, Subject, and focus the body. FromSelect
  // stores only the local part and reads draft.from once at mount, so seed before
  // openDraft(). Wrapped so the composer opens even if seeding fails.
  const openSupportRequest = async () => {
    dropdownService.close()
    try {
      await writingService.setFrom('boxes', 'standalone')
      await writingService.setTosFromBox(['support@boxes.email'])
      await writingService.setSubject('Support request', 'standalone')
    } catch {
      // best-effort prefill; the composer opens regardless
    }
    writeMessageModal.openDraft()
    setTimeout(() => {
      const el = document.querySelector('.editable-slate-element')
      if (el) el.focus()
    }, 250)
  }

  const homeMenuOptions = [
    {
      icon: <CogIcon style={iconSize} />,
      title: 'Settings',
      action: () => {
        commonsService.goToPage('/settings')
        dropdownService.close()
      },
      color: 'normal'
    },
    {
      separator: true
    },
    whatsNewItem,
    {
      icon: <ContactAltIcon style={iconSize} />,
      title: 'Support',
      action: openSupportRequest,
      color: 'normal'
    },
    {
      icon: <QuestionMarkIcon style={iconSize} />,
      title: 'FAQ',
      action: () => {
        modalService.open(<FaqModal/>)
        dropdownService.close()
      },
      color: 'normal'
    },
    boxCount > 0 ? { info: `${boxCount} boxes with ${labelCount} labels` } : null,
    {
      separator: true
    },
    {
      icon: <LogoutIcon style={iconSize} />,
      title: 'Sign Out',
      action: () => {
        authService.signOut()
      },
      color: 'red',
    },

  ].filter(a => a)

  const settingsMenuOptions = [
    (userStatus === 'active' || userStatus === 'trial_active')
      ? {
          icon: <HomeIcon style={iconSize} />,
          title: 'Home',
          action: () => {
            commonsService.goToPage('/', true)
            dropdownService.close()
          },
          color: 'black'
        }
      : null,
    (userStatus === 'active' || userStatus === 'trial_active')
      ? {
          separator: true
        }
      : null,
    whatsNewItem,
    {
      icon: <ContactAltIcon style={iconSize} />,
      title: 'Support',
      action: openSupportRequest,
      color: 'normal'
    },
    {
      icon: <QuestionMarkIcon style={iconSize} />,
      title: 'FAQ',
      action: () => {
        modalService.open(<FaqModal/>)
        dropdownService.close()
      },
      color: 'normal'
    },
    boxCount > 0 ? { info: `${boxCount} boxes with ${labelCount} labels` } : null,
    {
      separator: true
    },
    {
      icon: <LogoutIcon style={iconSize} />,
      title: 'Sign Out',
      action: () => {
        authService.signOut()
      },
      color: 'red',
    },
  ].filter(a => a)

  useEffect(() => {
    const options = window.location.href.includes('/settings') ? settingsMenuOptions : homeMenuOptions
    dropdownService.open(options, 'left header', event)
  }, [])

  return <></>
}
